import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, MapPin, Sparkles } from "lucide-react";
import AppointmentRequestModal from "../components/schedule/AppointmentRequestModal";
import WaitlistModal from "../components/schedule/WaitlistModal";
import { formatTimeRange } from "../lib/eventTime";

function normalizeBase(url) {
  return url?.replace(/\/$/, "");
}

function getApiBaseCandidates() {
  const explicitBase = normalizeBase(import.meta.env.VITE_APP_BASE_URL);
  const viteBase = normalizeBase(import.meta.env.VITE_API_BASE_URL);
  const appUrl = normalizeBase(import.meta.env.VITE_APP_API_URL);

  return [explicitBase, viteBase, appUrl ? `${appUrl}/api` : null, "/api/nextjs"].filter(Boolean);
}

function formatDate(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function isPastDate(dateValue) {
  if (!dateValue) return false;
  const time = new Date(dateValue).getTime();
  if (Number.isNaN(time)) return false;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  return time < todayStart.getTime();
}

// Turns YouTube watch / shorts / youtu.be / embed URLs into an embeddable URL; null for anything else
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "").replace(/^m\./, "");
    let videoId = null;

    if (host === "youtu.be") {
      videoId = parsed.pathname.slice(1).split("/")[0];
    } else if (host === "youtube.com" || host === "youtube-nocookie.com") {
      if (parsed.pathname === "/watch") {
        videoId = parsed.searchParams.get("v");
      } else if (parsed.pathname.startsWith("/shorts/") || parsed.pathname.startsWith("/embed/")) {
        videoId = parsed.pathname.split("/")[2];
      }
    }

    if (!videoId) return null;
    return `https://www.youtube-nocookie.com/embed/${videoId}`;
  } catch {
    return null;
  }
}

// Small gold eyebrow + maroon heading, matching the Impact/Schedule section language
const SectionHeading = ({ eyebrow, title }) => (
  <div>
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#b8860b]">
      {eyebrow}
    </p>
    <h2 className="text-3xl font-bold leading-tight text-[#5a0000] md:text-4xl">{title}</h2>
  </div>
);

// Renders a YouTube iframe when the URL is a YouTube link, otherwise a native video player
const VideoEmbed = ({ url, title }) => {
  const embedUrl = getYouTubeEmbedUrl(url);

  if (embedUrl) {
    return (
      <iframe
        src={embedUrl}
        title={title || "Event video"}
        className="h-full w-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
      />
    );
  }

  return (
    <video controls preload="metadata" className="h-full w-full object-contain" title={title}>
      <source src={url} />
      Your browser does not support embedded videos.
    </video>
  );
};

const DetailSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-[46vh] min-h-[320px] w-full bg-[#e9ddc8]" />
    <div className="container mx-auto px-4 py-14 sm:px-6 lg:px-8">
      <div className="h-4 w-40 rounded bg-[#e9ddc8]" />
      <div className="mt-4 h-8 w-2/3 rounded bg-[#e9ddc8]" />
      <div className="mt-8 rounded-2xl border border-[#efe4cf] bg-white p-6 shadow-sm">
        <div className="h-4 w-full rounded bg-[#f3ead8]" />
        <div className="mt-3 h-4 w-11/12 rounded bg-[#f3ead8]" />
        <div className="mt-3 h-4 w-4/5 rounded bg-[#f3ead8]" />
      </div>
      <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-3">
        {[0, 1, 2].map((n) => (
          <div key={n} className="aspect-[4/3] rounded-2xl bg-[#e9ddc8]" />
        ))}
      </div>
    </div>
  </div>
);

const DetailError = ({ message, onRetry }) => (
  <div className="container mx-auto flex min-h-[60vh] items-center justify-center px-4 py-24 sm:px-6 lg:px-8">
    <div className="w-full max-w-lg rounded-2xl border border-[#efe4cf] bg-white p-8 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#fbf3e6]">
        <Calendar className="h-7 w-7 text-[#8a0000]" />
      </div>
      <h1 className="mt-5 text-2xl font-bold text-[#5a0000]">We couldn't load this event</h1>
      <p className="mt-3 text-sm leading-7 text-gray-600">{message}</p>
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg bg-[#5a0000] px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-[#8a0000]"
        >
          Try again
        </button>
        <Link
          to="/upcoming-event"
          className="inline-flex items-center gap-2 rounded-lg border border-[#efe4cf] px-5 py-3 text-sm font-medium text-[#5a0000] transition-colors hover:border-[#5a0000]"
        >
          <ArrowLeft size={16} />
          All events
        </Link>
      </div>
    </div>
  </div>
);

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    async function loadEvent() {
      setLoading(true);
      setLoadError("");

      try {
        const eventApi = normalizeBase(import.meta.env.VITE_APP_EVENT_API);
        let payload;

        if (eventApi) {
          const response = await fetch(`${eventApi}/${id}`);
          payload = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(payload.message || "Unable to load this event");
        } else {
          const bases = getApiBaseCandidates();
          let lastError;
          for (const base of bases) {
            try {
              const response = await fetch(`${base}/events/${id}`);
              const data = await response.json().catch(() => ({}));
              if (!response.ok) throw new Error(data.message || "Unable to load this event");
              payload = data;
              lastError = null;
              break;
            } catch (error) {
              lastError = error;
            }
          }
          if (lastError) throw lastError;
        }

        if (cancelled) return;

        if (payload?.data && typeof payload.data === "object") {
          setEvent(payload.data);
        } else {
          setEvent(null);
          setLoadError("This event could not be found. It may have been removed.");
        }
      } catch (error) {
        if (!cancelled) {
          setEvent(null);
          setLoadError("Something went wrong while loading this event. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadEvent();

    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  // Registration — same shared modal + wiring as the Schedule and Upcoming Events pages
  const openEventRegistration = () => {
    if (!event?.booking) return;
    const formattedDate = formatDate(event.eventDate) || "Not Available";
    setSelectedScheduleItem({
      id: event.booking.scheduleId,
      type: "event",
      availableSeats: event.booking.availableSeats,
      location: event.eventLocation,
      startDate: formattedDate,
      endDate: formattedDate,
      registrationFields: event.registrationFields || [],
      registrationNote: event.registrationNote || "",
      showSeatsRemaining: event.showSeatsRemaining !== false,
      // Carries the venue time onto the registration record, so confirmation
      // emails say when to arrive and not just which day.
      time: formatTimeRange(event.startTime, event.endTime) || "",
      request: true,
    });
    setRegistrationModalOpen(true);
  };

  const handleCloseRegistration = () => {
    setRegistrationModalOpen(false);
    setSelectedScheduleItem(null);
  };

  const handleAppointmentSubmit = (appointmentData) => {
    console.log("Appointment submitted:", appointmentData);
    // You can add additional handling here, such as updating UI state
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[#FBF7EE] pt-20">
        <DetailSkeleton />
      </main>
    );
  }

  if (loadError || !event) {
    return (
      <main className="min-h-screen bg-[#FBF7EE] pt-20">
        <DetailError
          message={loadError || "This event could not be found."}
          onRetry={() => setReloadKey((key) => key + 1)}
        />
      </main>
    );
  }

  const isPast = isPastDate(event.eventDate);
  const booking = event.booking;
  const canRegister = !isPast && booking && !booking.isFull;
  const isFull = !isPast && booking && booking.isFull;
  const formattedDate = formatDate(event.eventDate);
  const timeRange = formatTimeRange(event.startTime, event.endTime);
  const galleryItems = (event.gallery || []).filter((item) => item?.src);
  const reels = (event.videos || []).filter((video) => video?.type === "reel" && video.url);
  const longVideos = (event.videos || []).filter((video) => video?.type === "long" && video.url);

  return (
    <main className="min-h-screen bg-[#FBF7EE]">
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-[#241511] pt-24 text-white lg:pt-28">
        {event.eventImage ? (
          <img
            src={event.eventImage}
            alt={event.eventName}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
        <div
          className="absolute inset-0 bg-gradient-to-t from-[#1c0a0a]/95 via-[#2a120d]/65 to-[#2a120d]/35"
          aria-hidden="true"
        />
        <div className="container relative z-10 mx-auto flex min-h-[46vh] flex-col justify-end px-4 pb-12 pt-16 sm:px-6 lg:min-h-[56vh] lg:px-8">
          <Link
            to="/upcoming-event"
            className="mb-6 inline-flex w-fit items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/75 transition hover:text-[#ffd700]"
          >
            <ArrowLeft size={15} />
            All events
          </Link>

          <div className="flex flex-wrap items-center gap-3">
            {isPast && (
              <span className="rounded-full bg-amber-400/95 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-[#3d2b00]">
                Past event
              </span>
            )}
            {formattedDate && (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#ffd700]">
                <Calendar size={16} className="shrink-0" />
                {formattedDate}
              </span>
            )}
            {timeRange && (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#ffd700]">
                <Clock size={16} className="shrink-0" />
                {timeRange}
              </span>
            )}
            {event.eventFormat?.trim() && (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#ffd700]">
                <Sparkles size={16} className="shrink-0" />
                {event.eventFormat.trim()}
              </span>
            )}
          </div>

          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight md:text-5xl">
            {event.eventName}
          </h1>

          {event.eventLocation && (
            <span className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-white/14 px-4 py-1.5 text-sm font-medium backdrop-blur">
              <MapPin size={15} className="shrink-0 text-[#ffd700]" />
              {event.eventLocation}
            </span>
          )}

          {(canRegister || isFull) && (
            <div className="mt-7 flex flex-wrap items-center gap-4">
              {canRegister ? (
                <>
                  <button
                    type="button"
                    onClick={openEventRegistration}
                    className="rounded-lg bg-[#5a0000] px-8 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white shadow-lg transition-colors hover:bg-[#8a0000]"
                  >
                    Register
                  </button>
                  {event.showSeatsRemaining !== false && (
                    <p className="text-sm text-white/80">
                      {booking.availableSeats} seat{booking.availableSeats === 1 ? "" : "s"}{" "}
                      remaining
                    </p>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setWaitlistOpen(true)}
                  className="rounded-lg border border-white/60 bg-white/10 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-white backdrop-blur transition hover:bg-white/20"
                >
                  Full — Join the waitlist
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* About */}
      <section className="container mx-auto px-4 py-14 sm:px-6 md:py-20 lg:px-8">
        <SectionHeading eyebrow="About" title="About this event" />
        <div className="mt-8 rounded-2xl border border-[#efe4cf] bg-white p-6 shadow-sm md:p-10">
          {event.description ? (
            <p className="whitespace-pre-line text-base leading-8 text-gray-700">
              {event.description}
            </p>
          ) : (
            <p className="text-base leading-8 text-gray-500">
              Details for this event will be shared soon.
            </p>
          )}
        </div>
      </section>

      {/* Gallery */}
      {galleryItems.length > 0 && (
        <section className="container mx-auto px-4 pb-14 sm:px-6 md:pb-20 lg:px-8">
          <SectionHeading eyebrow="Glimpses" title="Gallery" />
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-3">
            {galleryItems.map((item, index) => (
              <figure key={`${item.src}-${index}`} className="group">
                <div className="overflow-hidden rounded-2xl border border-[#efe4cf] bg-white shadow-sm">
                  <img
                    src={item.src}
                    alt={item.alt || item.caption || `${event.eventName} photo ${index + 1}`}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                {item.caption && (
                  <figcaption className="mt-2 text-sm leading-6 text-gray-600">
                    {item.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* Reels */}
      {reels.length > 0 && (
        <section className="container mx-auto px-4 pb-14 sm:px-6 md:pb-20 lg:px-8">
          <SectionHeading eyebrow="Short takes" title="Reels" />
          <div className="mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4">
            {reels.map((reel, index) => (
              <div
                key={`${reel.url}-${index}`}
                className="w-[230px] shrink-0 snap-start sm:w-[260px]"
              >
                <div className="aspect-[9/16] overflow-hidden rounded-2xl border border-[#efe4cf] bg-black shadow-sm">
                  <VideoEmbed url={reel.url} title={reel.title || `Reel ${index + 1}`} />
                </div>
                {reel.title && (
                  <p className="mt-2 text-sm font-medium leading-6 text-[#5a0000]">{reel.title}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Videos */}
      {longVideos.length > 0 && (
        <section className="container mx-auto px-4 pb-14 sm:px-6 md:pb-20 lg:px-8">
          <SectionHeading eyebrow="Watch" title="Videos" />
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {longVideos.map((video, index) => (
              <div key={`${video.url}-${index}`}>
                <div className="aspect-video overflow-hidden rounded-2xl border border-[#efe4cf] bg-black shadow-sm">
                  <VideoEmbed url={video.url} title={video.title || `Video ${index + 1}`} />
                </div>
                {video.title && (
                  <p className="mt-3 text-base font-semibold leading-7 text-[#5a0000]">
                    {video.title}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Back link */}
      <section className="container mx-auto px-4 pb-16 sm:px-6 md:pb-24 lg:px-8">
        <Link
          to="/upcoming-event"
          className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#8a0000] transition hover:text-[#5a0000]"
        >
          <ArrowLeft size={16} />
          All events
        </Link>
      </section>

      {registrationModalOpen && selectedScheduleItem && (
        <AppointmentRequestModal
          isOpen={registrationModalOpen}
          onClose={handleCloseRegistration}
          scheduleItem={selectedScheduleItem}
          onSubmit={handleAppointmentSubmit}
        />
      )}

      <WaitlistModal
        isOpen={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        scheduleId={event?.booking?.scheduleId}
        eventName={event?.eventName}
      />
    </main>
  );
}
